import React from 'react';
import styled from 'styled-components';
import { FiX, FiMail, FiCalendar } from 'react-icons/fi';
import { GiCoral, GiTropicalFish } from 'react-icons/gi';
import { Button, Badge } from '../styles/GlobalStyles';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.ocean};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${props => props.theme.colors.textLight};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${props => props.theme.borderRadius.small};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.primary};
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const ProductImage = styled.div`
  width: 100%;
  height: 300px;
  background: ${props => props.image ? 
    `url(${props.image}) center/cover` : 
    props.theme.gradients.wave};
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-bottom: 2rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => !props.image ? 
      'rgba(0, 105, 148, 0.1)' : 
      'linear-gradient(45deg, transparent 0%, rgba(0, 0, 0, 0.1) 100%)'};
  }
`;

const PlaceholderIcon = styled.div`
  font-size: 6rem;
  color: white;
  z-index: 2;
  opacity: 0.8;
`;

const ProductHeader = styled.div`
  margin-bottom: 2rem;
`;

const ProductName = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const ScientificName = styled.p`
  font-style: italic;
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
  margin-bottom: 1rem;
`;

const ProductDescription = styled.div`
  color: ${props => props.theme.colors.text};
  line-height: 1.8;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const ProductDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 2rem;
  line-height: 1;
`;

const ProductMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 2rem;
`;

const ProductPrice = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1rem 2rem 2rem;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const getDifficultyColor = (level) => {
  switch(level) {
    case 'Anfänger': return 'emerald';
    case 'Mittel': return 'amber';
    case 'Fortgeschritten': return 'error';
    default: return 'info';
  }
};

const getTemperamentColor = (temperament) => {
  switch(temperament) {
    case 'Friedlich': return 'emerald';
    case 'Semi-Aggressiv': return 'amber';
    case 'Aggressiv': return 'error';
    default: return 'info';
  }
};

const getSizeColor = (size) => {
  if (!size) return 'neutral';
  const sizeStr = size.toLowerCase();
  if (sizeStr.includes('klein') || sizeStr.includes('small') || sizeStr.includes('s')) return 'teal';
  if (sizeStr.includes('mittel') || sizeStr.includes('medium') || sizeStr.includes('m')) return 'indigo';
  if (sizeStr.includes('groß') || sizeStr.includes('large') || sizeStr.includes('l')) return 'purple';
  if (sizeStr.includes('xl') || sizeStr.includes('sehr')) return 'pink';
  return 'ocean';
};

const getDietColor = (diet) => {
  if (!diet) return 'neutral';
  const dietStr = diet.toLowerCase();
  if (dietStr.includes('fleisch') || dietStr.includes('carnivore')) return 'error';
  if (dietStr.includes('pflanz') || dietStr.includes('herbivore')) return 'emerald';
  if (dietStr.includes('alles') || dietStr.includes('omnivore')) return 'amber';
  return 'coral';
};

const getColorVariant = (colorName) => {
  if (!colorName) return 'neutral';
  const color = colorName.toLowerCase();
  if (color.includes('rot') || color.includes('red')) return 'error';
  if (color.includes('blau') || color.includes('blue')) return 'ocean';
  if (color.includes('grün') || color.includes('green')) return 'emerald';
  if (color.includes('gelb') || color.includes('yellow')) return 'amber';
  if (color.includes('lila') || color.includes('purple') || color.includes('violett')) return 'purple';
  if (color.includes('rosa') || color.includes('pink')) return 'pink';
  if (color.includes('orange')) return 'coral';
  if (color.includes('braun') || color.includes('brown')) return 'slate';
  return 'coral';
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatPrice = (price) => {
  if (!price) return null;
  // If price already contains CHF, return as is
  if (price.includes('CHF')) return price;
  // Otherwise, add CHF prefix
  return `CHF ${price}`;
};

const ProductModal = ({ item, type, onClose, onContact }) => {
  if (!item) return null;

  const isCoralType = type === 'coral';

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {isCoralType ? <GiCoral size={32} /> : <GiTropicalFish size={32} />}
            <span style={{ fontSize: '1.2rem', fontWeight: '600', color: '#006994' }}>
              {isCoralType ? 'Koralle Details' : 'Fisch Details'}
            </span>
          </div>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ProductImage image={item.image_url}>
            {!item.image_url && (
              <PlaceholderIcon>
                {isCoralType ? <GiCoral /> : <GiTropicalFish />}
              </PlaceholderIcon>
            )}
          </ProductImage>

          <ProductHeader>
            <ProductName>{item.name}</ProductName>
            {item.scientific_name && (
              <ScientificName>{item.scientific_name}</ScientificName>
            )}
          </ProductHeader>

          <ProductDescription>
            {item.description}
          </ProductDescription>

          <ProductDetails>
            {/* Availability Status Badge */}
            <Badge variant={item.available ? 'success' : 'error'} style={{ fontSize: '1rem' }} noTransform>
              {item.available ? 'Verfügbar' : 'Nicht verfügbar'}
            </Badge>
            
            {isCoralType ? (
              <>
                {item.size && <Badge variant={getSizeColor(item.size)} style={{ fontSize: '1rem' }}>{item.size}</Badge>}
                {item.color && <Badge variant={getColorVariant(item.color)} style={{ fontSize: '1rem' }}>{item.color}</Badge>}
                {item.difficulty_level && (
                  <Badge variant={getDifficultyColor(item.difficulty_level)} style={{ fontSize: '1rem' }}>
                    {item.difficulty_level}
                  </Badge>
                )}
              </>
            ) : (
              <>
                {item.size && <Badge variant={getSizeColor(item.size)} style={{ fontSize: '1rem' }}>{item.size}</Badge>}
                {item.temperament && (
                  <Badge variant={getTemperamentColor(item.temperament)} style={{ fontSize: '1rem' }}>
                    {item.temperament}
                  </Badge>
                )}
                {item.diet && <Badge variant={getDietColor(item.diet)} style={{ fontSize: '1rem' }}>{item.diet}</Badge>}
                {item.tank_size_min && (
                  <Badge variant="ocean" style={{ fontSize: '1rem' }} noTransform>
                    Min. {item.tank_size_min}L
                  </Badge>
                )}
              </>
            )}
          </ProductDetails>

          {/* Price Display */}
          {item.price && (
            <ProductPrice>
              {formatPrice(item.price)}
            </ProductPrice>
          )}

          <ProductMeta>
            <FiCalendar size={18} />
            Verfügbar seit: {formatDate(item.available_since || item.created_at)}
          </ProductMeta>
        </ModalBody>

        <ModalActions>
          <Button
            onClick={() => onContact(item)}
            disabled={!item.available}
            secondary={!item.available}
          >
            <FiMail size={16} />
            {item.available ? 'Interesse zeigen' : 'Nicht verfügbar'}
          </Button>
          <Button outline onClick={onClose}>
            Schließen
          </Button>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ProductModal;
